interface SortOptions {
  votetype: string;
  default_sort: string;
  commentcount: number;
  new_comments: boolean;
}

// helpers to initialize filter values
import { projectStore } from '@/store/project-store';
import { computed } from 'vue';
import useConverter from '../general/useConverter';
import { ITopic } from '@/models/topic.model';
import { IProject } from '@/models/project.model';
export default function useThoughtFilter() {
  const { toBool } = useConverter();

  const project = computed(() => {
    return projectStore.getState().project;
  });

  const voteConfig = computed(() => {
    if (project.value?.config?.show_sort_votecount !== undefined) {
      return toBool(project.value.config.show_sort_votecount);
    } else {
      return true;
    }
  });

  const commentConfig = computed(() => {
    if (project.value?.config?.show_sort_comment !== undefined) {
      return toBool(project.value.config.show_sort_comment);
    } else {
      return true;
    }
  });

  function getDefaultFilterFields() {
    return {
      text: {
        name: 'Text',
        type: 'text',
        ordering: 1,
        default_value: null,
        visible: true,
      },
      sort: {
        name: 'Sortierung',
        type: 'valuelist',
        default_value: 'created',
        values: [] as object[],
        visible: true,
        required: true,
      },
    };
  }

  function getWeightOptionsFromTopics(topics: Array<ITopic>) {
    const optionsHash: any = {};
    topics.forEach((t: any) => {
      const weights = t.vote_weight_options;
      if (weights && weights.length > 0) {
        weights.forEach((w: number) => {
          optionsHash[w] = 1;
        });
      }
    });
    return Object.keys(optionsHash).sort();
  }

  function getThoughtWeightField(topics: Array<ITopic>, weightNameMap: any) {
    if (topics.length == 0) {
      return null;
    }
    if (topics.find((x: any) => x.votetype == 'weighted_support') == undefined) {
      return null;
    }
    if (weightNameMap == null || weightNameMap == undefined) {
      weightNameMap = {};
    }
    const voteWeightOptions = getWeightOptionsFromTopics(topics);
    const options = voteWeightOptions.map(opt => {
      let optText = weightNameMap ? weightNameMap[opt] : undefined;
      if (optText == undefined) {
        optText = opt;
      }
      return { id: opt, text: optText, ordering: opt };
    });
    return {
      name: 'Schwierigkeit',
      type: 'valuelist',
      default_value: 0,
      visible: true,
      filter_name: 'vote_weight',
      values: options,
      allowClear: true,
    };
  }

  function getSortOptions(options: SortOptions) {
    const sortOptions = [];
    let defaultSort = 'random';
    if (options.votetype !== 'none' && voteConfig.value) {
      sortOptions.push({
        id: 'votecount',
        ordering: 1,
        text: 'Anzahl Stimmen',
      });
      if (options.default_sort == 'votecount') {
        defaultSort = options.default_sort;
      }
      if (options.votetype !== 'support' && options.votetype != 'weighted_support') {
        sortOptions.push({
          id: 'votesum',
          ordering: 2,
          text: 'Zustimmung',
        });
        if (options.default_sort == 'votesum') {
          defaultSort = options.default_sort;
        }
      }
    }

    if (options.votetype == 'weighted_support') {
      // T22744
      sortOptions.push({
        id: 'vote_weight',
        ordering: 3,
        text: 'Schwierigkeit (schwer zuerst)',
      });
      if (options.default_sort == 'vote_weight') {
        defaultSort = options.default_sort;
      }
      sortOptions.push({
        id: '+vote_weight',
        ordering: 4,
        text: 'Schwierigkeit (einfach zuerst)',
      });
      if (options.default_sort == '+vote_weight') {
        defaultSort = options.default_sort;
      }
    }

    if ((options.new_comments || options.commentcount > 0) && commentConfig.value) {
      sortOptions.push({
        id: 'commentcount',
        ordering: 3,
        text: 'Anzahl Kommentare',
      });
      if (options.default_sort == 'commentcount') {
        defaultSort = options.default_sort;
      }
    }

    sortOptions.push({
      id: 'created',
      ordering: 4,
      text: 'Zeit (neueste zuerst)',
    });
    if (options.default_sort == 'created') {
      defaultSort = options.default_sort;
    }

    // T21501, sort option for time ascending (oldest to newest)
    sortOptions.push({
      id: '+created',
      ordering: 4,
      text: 'Zeit (älteste zuerst)',
    });
    if (options.default_sort == '+created') {
      defaultSort = options.default_sort;
    }

    // T18783: Sort order random
    sortOptions.push({
      id: 'random',
      ordering: 5,
      text: 'Zufällig',
    });
    if (options.default_sort == 'random') {
      defaultSort = options.default_sort;
    }

    return {
      values: sortOptions,
      default: defaultSort,
    };
  }

  return {
    getDefaultFilterFields,
    getSortOptions,
    getWeightOptionsFromTopics,
    getThoughtWeightField,
  };
}
